/*
  Shopify injects its native Customer Privacy consent banner (#shopify-pc__banner)
  into the DOM asynchronously and only for visitors whose region requires it.

  This script:
  - moves the banner to be the very first element in <body>, so it can use
    position: sticky (see cookie-banner.css) instead of position: fixed —
    with sticky + first-in-flow, the page content below it reflows natively
    whenever the banner's height changes, with no manual offset bookkeeping.
    The observer is disconnected for the duration of that move so it can't
    see (and react to) its own remove+insert mutation records.
  - ties the banner's "compactness" directly to scroll position via a single
    --cb-t custom property (0 = fully expanded, 1 = fully compact), read by
    cookie-banner.css through calc(). Because it's driven straight off
    scrollY on every scroll event (no CSS transition racing behind it), the
    collapse tracks the scroll 1:1 with no separate animation to feel
    delayed or jerky.
  - lets tapping the compact bar (not its buttons) force it open, staying
    open until the visitor scrolls again (which ends the peek immediately)
    or clicks one of its buttons — no fixed timer forcing it shut on its
    own while they're just sitting there reading it.
  - toggles a discrete .is-scrolled class the moment any scrolling starts,
    which switches the description in cookie-banner.css from its full
    wrapped text to a single ellipsized line that then narrows continuously
    with --cb-t. Keeping this one switch discrete (rather than driving it
    off --cb-t directly) means the banner shows the complete disclosure at
    rest and only starts trimming it once the page is actually moving.
  - once the banner is compact enough that "Manage preferences" and the
    Privacy Policy link are visually collapsed (width near 0), takes them
    out of the tab order — that alone doesn't stop keyboard focus, so
    without this a Tab-only user would land on invisible controls.
  - collapses "Manage preferences" over its own, much shorter scroll
    distance (BTN_COMPACT_RANGE) via a second custom property, --cb-bt —
    it disappears well before the text finishes narrowing, rather than
    shrinking at the same pace. --cb-bt is just --cb-t rescaled so it
    reaches 1 at BTN_COMPACT_RANGE px of scroll instead of COMPACT_RANGE.
  - publishes its own current rendered height as --cookie-banner-height, so
    cookie-banner.css can push other sticky top-anchored elements (a sticky
    header, a sticky hero slide, a sticky filter sidebar) down below it
    instead of letting them render underneath it.
*/
(() => {
  const BANNER_SELECTOR = '#shopify-pc__banner';
  const COMPACT_RANGE = 80; // px of scroll over which the banner fully compacts
  const BTN_COMPACT_RANGE = 24; // px of scroll over which "Manage preferences" fully collapses
  const INERT_THRESHOLD = 0.9; // --cb-t / --cb-bt past which collapsed controls are tabbed out
  const SCROLLED_THRESHOLD = 0.03; // --cb-t past which the paragraph switches to a single ellipsized line
  const SCROLL_QUIET_MS = 400; // a peek only cancels once scrolling has been quiet this long

  let bannerEl = null;
  let managePrefsEl = null;
  let policyLinkEl = null;
  let forcedExpand = false;
  let graceTimer = null;
  let ticking = false;
  let isManageCompact = false;
  let isPolicyInert = false;
  let isScrolled = false;
  let heightObserver = null;

  const setManageCompact = (shouldBeCompact) => {
    if (shouldBeCompact === isManageCompact) return;
    isManageCompact = shouldBeCompact;
    if (managePrefsEl) {
      if (shouldBeCompact) {
        managePrefsEl.setAttribute('tabindex', '-1');
        managePrefsEl.setAttribute('aria-hidden', 'true');
      } else {
        managePrefsEl.removeAttribute('tabindex');
        managePrefsEl.removeAttribute('aria-hidden');
      }
    }
    // Also fully removes "Manage preferences" from display at the same
    // threshold (see cookie-banner.css .is-compact rule) — shrinking it to
    // ~0 via the calc()s alone still leaves a sliver of its border/padding
    // and a clipped fragment of its ellipsized label visible.
    bannerEl.classList.toggle('is-compact', shouldBeCompact);
  };

  const setPolicyInert = (shouldBeInert) => {
    if (shouldBeInert === isPolicyInert) return;
    isPolicyInert = shouldBeInert;
    if (!policyLinkEl) return;
    if (shouldBeInert) {
      policyLinkEl.setAttribute('tabindex', '-1');
      policyLinkEl.setAttribute('aria-hidden', 'true');
    } else {
      policyLinkEl.removeAttribute('tabindex');
      policyLinkEl.removeAttribute('aria-hidden');
    }
    // Unlike Manage preferences, the link's own clipping (cookie-banner.css
    // .is-scrolled p rule) is max-height/overflow only, which doesn't drop it
    // from the tab order — so a keyboard user who tabbed onto it before
    // scrolling could be left focused on a now aria-hidden, invisible link.
    // display:none forces the browser to blur it, same as .is-compact does
    // for Manage preferences. Toggled on the link itself (not a bannerEl-wide
    // class matched against a bare "a" selector), so this can never catch
    // some other, unrelated link if Shopify's markup ever adds one.
    policyLinkEl.classList.toggle('is-policy-link-inert', shouldBeInert);
  };

  // At rest (t=0) the description wraps across its natural lines in full —
  // switching straight to a width-driven single ellipsized line (see
  // cookie-banner.css) only once scrolling actually starts, rather than
  // truncating it even when the page hasn't moved.
  const setScrolled = (shouldBeScrolled) => {
    if (shouldBeScrolled === isScrolled) return;
    isScrolled = shouldBeScrolled;
    bannerEl.classList.toggle('is-scrolled', shouldBeScrolled);
  };

  // The banner is sticky and first-in-body, so its own rendered height is
  // also the amount every *other* sticky top-anchored element (a sticky
  // header, a sticky hero slide, a sticky filter sidebar) needs to be pushed
  // down by to not render underneath it — see the --cookie-banner-height
  // consumers in cookie-banner.css. Driven solely by the ResizeObserver in
  // attach() (not called directly from setT()): reading offsetHeight right
  // after setT() writes styles that affect box size would force a synchronous
  // layout on every scroll-driven animation frame, and the observer already
  // catches every one of those size changes on the browser's own schedule.
  const updateBannerHeightVar = () => {
    if (!bannerEl) return;
    document.documentElement.style.setProperty('--cookie-banner-height', `${bannerEl.offsetHeight}px`);
  };

  const setT = (t) => {
    if (!bannerEl) return;
    bannerEl.style.setProperty('--cb-t', t.toFixed(3));
    const bt = Math.min(1, t * (COMPACT_RANGE / BTN_COMPACT_RANGE));
    bannerEl.style.setProperty('--cb-bt', bt.toFixed(3));
    setManageCompact(bt >= INERT_THRESHOLD);
    // Driven by SCROLLED_THRESHOLD, not INERT_THRESHOLD: unlike Manage
    // preferences (which shrinks gradually and only needs to leave the tab
    // order once it's visually near-zero), the policy link's containing
    // paragraph collapses to zero height in one step as soon as .is-scrolled
    // applies — the same point isScrolled/setScrolled below reacts to.
    // Reusing INERT_THRESHOLD here left the link tabbable-but-invisible for
    // the ~2.4px..72px scroll range in between.
    setPolicyInert(t >= SCROLLED_THRESHOLD);
    setScrolled(t >= SCROLLED_THRESHOLD);
  };

  const currentT = () => {
    const y = window.scrollY || window.pageYOffset || 0;
    return Math.min(1, Math.max(0, y / COMPACT_RANGE));
  };

  const cancelPeek = () => {
    forcedExpand = false;
    window.clearTimeout(graceTimer);
    graceTimer = null;
    setT(currentT());
  };

  const onScroll = () => {
    // Every scroll event while peeking (re)schedules the cancel for
    // SCROLL_QUIET_MS later, rather than cancelling on the spot or against a
    // fixed deadline counted from the tap. A mouse wheel or trackpad keeps
    // sending real scroll events for a while after the visitor's hand has
    // actually stopped — momentum/inertia scrolling that commonly outlasts
    // any fixed grace window, especially since the scroll that revealed the
    // compact bar in the first place is often still decaying when the tap
    // lands a moment later. Rescheduling on every event means the peek
    // survives the *entire* tail, however long it runs, and only closes
    // once scrolling has been genuinely quiet for a full SCROLL_QUIET_MS —
    // rather than closing mid-momentum before there's been any chance to
    // read it.
    if (forcedExpand) {
      window.clearTimeout(graceTimer);
      graceTimer = window.setTimeout(() => {
        if (forcedExpand) cancelPeek();
      }, SCROLL_QUIET_MS);
    }
    if (ticking || !bannerEl) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      if (!bannerEl || forcedExpand) return;
      setT(currentT());
    });
  };

  const onBannerClick = (event) => {
    // Also allow tapping to peek whenever Manage preferences is already
    // hidden (isManageCompact), even if the banner as a whole isn't past
    // its own 50% threshold yet — that button now collapses over a much
    // shorter distance (BTN_COMPACT_RANGE) than this 0.5 was tuned for, so
    // without this a tap couldn't bring it back for a stretch of scroll
    // positions where it's gone but the rest of the banner isn't "half
    // compact" yet.
    if (currentT() < 0.5 && !isManageCompact) return;
    if (event.target.closest('button, a')) return;

    // Stays open until a scroll ends it (only once scrolling has actually
    // gone quiet — see onScroll) or a button click does — no fixed timer
    // forcing it shut while the visitor is still reading it. No timer is
    // scheduled here at all: sitting completely still after the tap must
    // leave it open indefinitely, and onScroll is the only thing that ever
    // starts a cancel countdown, exactly once a scroll actually happens.
    forcedExpand = true;
    window.clearTimeout(graceTimer);
    graceTimer = null;

    // setT(0) below grows the banner back to its full height. Since it's
    // sticky and first-in-body, that reflows everything after it — which is
    // exactly the layout shift CSS scroll anchoring exists to smooth over,
    // and it does so with a real, dispatched 'scroll' event. onScroll can't
    // tell that apart from a genuine scroll, so without this it schedules a
    // cancel via the exact same path as a real scroll would, even though the
    // visitor never touched their scroll wheel/trackpad. Suppressing
    // anchoring for the couple of frames this reflow takes avoids that
    // self-inflicted cancel.
    document.documentElement.style.overflowAnchor = 'none';
    setT(0);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.documentElement.style.overflowAnchor = '';
      });
    });
  };

  // Declared before attach()/detach() so it's already initialized whenever
  // they run, even though they're only called later (function bodies close
  // over it regardless of textual order — but attach() calls observer
  // methods directly, so this avoids any doubt).
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.id === 'shopify-pc__banner') attach(node);
        else if (node.querySelector) {
          const found = node.querySelector(BANNER_SELECTOR);
          if (found) attach(found);
        }
      });
      mutation.removedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        // node.contains(bannerEl) (not just node === bannerEl) so a removal
        // that discards the banner as part of a larger torn-down wrapper —
        // rather than removing #shopify-pc__banner directly — still detaches
        // it, mirroring the recursive querySelector check addedNodes gets
        // above. Otherwise bannerEl/heightObserver/the scroll listener would
        // keep referencing a now-detached node indefinitely.
        if (bannerEl && (node === bannerEl || node.contains(bannerEl))) detach();
      });
    }
  });

  function attach(el) {
    if (bannerEl === el) return;
    bannerEl = el;
    managePrefsEl = bannerEl.querySelector('.shopify-pc__banner__btn-manage-prefs');
    policyLinkEl = bannerEl.querySelector('.shopify-pc__banner__body a');
    isManageCompact = false;
    isPolicyInert = false;
    isScrolled = false;

    const h2 = bannerEl.querySelector('h2');
    if (h2 && h2.textContent.trim()) {
      // Redundant fallback: some older WebKit versions have dropped
      // display: contents subtrees from the accessibility tree, which would
      // orphan the section's aria-labelledby reference to this h2.
      bannerEl.setAttribute('aria-label', h2.textContent.trim());
    }

    if (document.body.firstElementChild !== bannerEl) {
      // Disconnect first: moving an already-attached node via insertBefore
      // queues its own remove+insert mutation records, and this same
      // observer would otherwise see the "remove" half and call detach()
      // on the node we're in the middle of attaching.
      observer.disconnect();
      document.body.insertBefore(bannerEl, document.body.firstChild);
      observer.observe(document.body, { childList: true, subtree: true });
    }

    setT(currentT());
    bannerEl.addEventListener('click', onBannerClick);
    window.addEventListener('scroll', onScroll, { passive: true });

    // Catches height changes setT() itself wouldn't know about — e.g. a
    // viewport resize re-wrapping the description onto a different number
    // of lines at the same scroll position/--cb-t.
    if (window.ResizeObserver) {
      heightObserver = new ResizeObserver(updateBannerHeightVar);
      heightObserver.observe(bannerEl);
    }
  }

  function detach() {
    if (bannerEl) bannerEl.removeEventListener('click', onBannerClick);
    window.removeEventListener('scroll', onScroll);
    window.clearTimeout(graceTimer);
    graceTimer = null;
    if (heightObserver) {
      heightObserver.disconnect();
      heightObserver = null;
    }
    document.documentElement.style.removeProperty('--cookie-banner-height');
    bannerEl = null;
    managePrefsEl = null;
    policyLinkEl = null;
    forcedExpand = false;
    isManageCompact = false;
    isPolicyInert = false;
    isScrolled = false;
  }

  const existing = document.querySelector(BANNER_SELECTOR);
  if (existing) attach(existing);

  observer.observe(document.body, { childList: true, subtree: true });
})();
